import { shortDate } from '@lib/time';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingRows, PageHeader } from '@shared/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import { useClients } from '../hooks/use-clients';
import { describeTarget, targetParams } from '../schemas/target';

import { searchClients } from './client-search';
import { EnvironmentScope } from './EnvironmentScope';

import type { ClientSummary } from '../schemas/client';

/**
 * The clients in one environment.
 *
 * ## What is NOT a column here
 *
 * The owner. The catalog knows about clients; people live in identity-api's
 * access store, and there is no read that lists them per client. A column that
 * is empty on every row is worse than no column, so the owner appears on the
 * detail screen — where a single client can be asked about — and this list
 * shows only what the catalog can actually answer.
 *
 * `status` is shown for the same reason it exists upstream, with a caveat worth
 * knowing: a client whose owner failed to provision is `active` here like any
 * other. The catalog cannot tell the two apart.
 */
export function ClientsPage() {
    const targetState = useClientTarget();
    const { target, isSlotMissing } = targetState;
    const clients = useClients(target);

    const [search, setSearch] = useState('');

    const all = clients.data ?? [];
    const shown = searchClients(all, search);

    return (
        <>
            <PageHeader
                eyebrow="Tenants"
                title="Clients"
                description="Every client with access to the platform. The ones that arrive through Salesforce register themselves; the rest are created here."
                actions={
                    <Link
                        to={{
                            pathname: '/clients/new',
                            search: new URLSearchParams(targetParams(target)).toString(),
                        }}
                    >
                        <Button>New client</Button>
                    </Link>
                }
            />

            <EnvironmentScope state={targetState} />

            {/* The search lives INSIDE the card, and that is the whole point: a
                control row floating above a panel with its own header reads as
                two objects, and an operator has to work out that the top one
                acts on the bottom one. Controls that narrow a table belong to
                that table's frame. */}
            <Card>
                <div className="flex items-center gap-3 border-b border-line-subtle px-5 py-3.5">
                    {/* The old card header said "Clients" a second time, under a
                        page already titled that. The count is what it carried
                        that the title did not, so the count is what survives. */}
                    <p className="text-[12.5px] text-content-muted">
                        {clients.data ? countLabel(shown.length) : 'Loading…'}
                    </p>

                    <input
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                        }}
                        placeholder="Search by name or address"
                        aria-label="Search clients"
                        className="ml-auto w-64 rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] outline-none transition-colors placeholder:text-content-faint focus:border-brand focus:ring-2 focus:ring-accent-surface"
                    />
                </div>

                <ClientsTable
                    state={targetState}
                    query={clients}
                    rows={shown}
                    slotMissing={isSlotMissing}
                />
            </Card>
        </>
    );
}

const countLabel = (count: number) => (count === 1 ? '1 client' : `${String(count)} clients`);

function ClientsTable({
    state,
    query,
    rows,
    slotMissing,
}: {
    state: ReturnType<typeof useClientTarget>;
    query: ReturnType<typeof useClients>;
    rows: ClientSummary[];
    slotMissing: boolean;
}) {
    // Three different answers, and they lead to three different actions:
    // choose a target, create the first client, or fix what broke.
    if (slotMissing) {
        return (
            <EmptyState
                title="No slot chosen"
                description="Pick a slot above to point at one of the ephemeral deployments."
            />
        );
    }

    if (query.isPending) return <LoadingRows label="Loading clients…" />;
    if (query.isError) {
        return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
    }

    if (query.data.length === 0) {
        return (
            <EmptyState
                title={`No clients in ${describeTarget(state.target)}`}
                description="Nothing has been created in this environment yet. Check the environment above if you expected to see something."
            />
        );
    }

    // Three empty states, not one. "This environment has none" and "your search
    // hid them all" send the operator to different controls, and saying the
    // wrong one sends them looking in the wrong place. The branch above already
    // answered the first, so reaching here means the search is what is hiding
    // them — there is no filter left that could be.
    if (rows.length === 0) {
        return (
            <EmptyState
                title="Nothing matches"
                description="No client in this environment matches that search. Clear it to see them all."
            />
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
                <thead>
                    <tr>
                        <Th>Client</Th>
                        <Th>Address</Th>
                        <Th>Status</Th>
                        <Th>Owner</Th>
                        <Th>Created</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((client) => (
                        <Row key={client.tenantId} client={client} state={state} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Th({ children }: { children: React.ReactNode }) {
    return (
        <th className="whitespace-nowrap border-b border-line-subtle bg-surface-sunken px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-content-faint">
            {children}
        </th>
    );
}

function Row({
    client,
    state,
}: {
    client: ClientSummary;
    state: ReturnType<typeof useClientTarget>;
}) {
    const search = new URLSearchParams(targetParams(state.target)).toString();

    return (
        <tr className="hover:bg-surface-sunken">
            <td className="border-b border-line-subtle px-5 py-3">
                <Link
                    to={{ pathname: `/clients/${client.tenantId}`, search }}
                    className="font-semibold text-content hover:text-accent-text hover:underline"
                >
                    {client.displayName}
                </Link>
            </td>
            <td className="border-b border-line-subtle px-5 py-3">
                {/* Absent is not empty: a client created before the slug existed
                    has none, and saying so beats a blank cell. */}
                {client.slug ? (
                    <span className="font-mono text-[12.5px] text-content-body">{client.slug}</span>
                ) : (
                    <span className="text-[12.5px] text-content-faint">None</span>
                )}
            </td>
            <td className="border-b border-line-subtle px-5 py-3">
                {client.isSandbox === true ? (
                    <Badge tone="warning" dot>
                        Sandbox
                    </Badge>
                ) : (
                    <Badge tone="active" dot>
                        {client.status}
                    </Badge>
                )}
            </td>
            <td className="border-b border-line-subtle px-5 py-3">
                <OnboardingCell client={client} search={search} />
            </td>
            <td className="border-b border-line-subtle px-5 py-3 text-[12.5px] text-content-muted">
                {shortDate(client.createdAt)}
            </td>
        </tr>
    );
}

/**
 * Three outcomes, and the third is the one that matters most.
 *
 * An absent answer gets a muted dash, not a tick and not a warning: the
 * directory could not be asked, so the honest report is that we do not know.
 * Either substitute would be a claim, and the wrong one costs an operator a
 * trip to fix something that works.
 */
function OnboardingCell({ client, search }: { client: ClientSummary; search: string }) {
    if (client.hasOwner === false) {
        return (
            <Link to={{ pathname: `/clients/${client.tenantId}/owner`, search }}>
                <Badge tone="warning" dot>
                    Needs owner
                </Badge>
            </Link>
        );
    }

    if (client.hasOwner === true) {
        return (
            <Badge tone="success" dot>
                Has owner
            </Badge>
        );
    }

    return (
        <span
            className="text-[12.5px] text-content-faint"
            title="The directory could not be reached — this is not a claim about the client"
        >
            —
        </span>
    );
}
